import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert, Avatar, Button, Chip, Grid, MenuItem, Stack, Tab, Tabs, TextField,
  Typography, FormControlLabel, Switch, Box, FormHelperText,
} from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import FormFileUpload from '../ui/forms/FormFileUpload';
import PersonNameField from '../ui/forms/PersonNameField';
import PhoneWithDialCodeField from '../ui/forms/PhoneWithDialCodeField';
import FormDrawer from '../ui/FormDrawer';
import ZoneMasterSelect from '../masters/ZoneMasterSelect';
import { getMasters } from '../../services/platform/masterService';
import { listPartners, getPartnerDocuments } from '../../services/partnerUserService';
import { listPartnerSubscriptionPlans } from '../../services/partnerSubscriptionPlanService';
import { resolveAssetUrl } from '../../utils/assetUrl';
import { FOOD_IMAGE_SPECS } from '../../constants/foodMasterImageSpecs';
import {
  masterImageUrl, resolveRestaurantImagesFromMasters, openingHoursFromMasters,
  type OpeningHourRow, type FoodMasterRow,
} from '../../utils/foodMasterImages';
import {
  emptyRestaurantForm, restaurantRowToForm, restaurantFormToPayload,
  RESTAURANT_MASTER_TABS, VERIFICATION_STATUS_OPTIONS, WEEK_DAYS,
  findRestaurantDuplicateErrors, normalizeRestaurantSlug,
  type RestaurantFormState,
} from './restaurantMasterTypes';
import RestaurantLocationPicker from './RestaurantLocationPicker';
import { extractMerchantCompliance, hasMerchantCompliance } from '../../utils/merchantComplianceExtract';
import { formatMoney } from '../../utils/currency';
import { useTenantCurrency } from '../../hooks/useTenantCurrency';
import { validateLocalPhoneForCountry, validateEmail } from '../../utils/personFieldValidation';
import { phoneLengthForDialCode } from '../../services/countryDialCodeService';
import { localPhoneDigits } from '../../utils/phoneDialCode';
import { welcomeEmailConfig, welcomeEmailHelper } from '../../config/welcomeEmail';
import { getWelcomeEmailConfig } from '../../services/welcomeEmailService';

export type { RestaurantFormState };
export { emptyRestaurantForm, restaurantRowToForm, restaurantFormToPayload };

async function loadFoodMasters(type: string): Promise<FoodMasterRow[]> {
  const res = await getMasters(type, { limit: 300 });
  return (res.data || []) as FoodMasterRow[];
}

interface RestaurantAddDrawerProps {
  open: boolean;
  editing: Record<string, unknown> | null;
  form: RestaurantFormState;
  onFormChange: (next: RestaurantFormState) => void;
  onClose: () => void;
  onSubmit: () => void;
  loading?: boolean;
  errorMessage?: string | null;
  serverFieldErrors?: Record<string, string>;
  existingRestaurants?: Array<{ _id?: string; name?: string; slug?: string }>;
}

export default function RestaurantAddDrawer({
  open, editing, form, onFormChange, onClose, onSubmit, loading, errorMessage,
  serverFieldErrors = {}, existingRestaurants = [],
}: RestaurantAddDrawerProps) {
  const { fieldLabel } = useTenantCurrency();
  const [tab, setTab] = useState(0);
  const [touched, setTouched] = useState<{
    name?: boolean; slug?: boolean; phone?: boolean; supportPhone?: boolean; email?: boolean; description?: boolean;
  }>({});
  const patch = (partial: Partial<RestaurantFormState>) => onFormChange({ ...form, ...partial });

  const { data: welcomeConfig } = useQuery({
    queryKey: ['welcome-email-config'],
    queryFn: getWelcomeEmailConfig,
    enabled: open,
    staleTime: 60_000,
  });

  const { data: masters, isLoading: mastersLoading } = useQuery({
    queryKey: ['restaurant-add-masters'],
    queryFn: async () => ({
      restaurant_types: await loadFoodMasters('restaurant_types'),
      cuisine_types: await loadFoodMasters('cuisine_types'),
      restaurant_branches: await loadFoodMasters('restaurant_branches'),
      restaurant_opening_hours: await loadFoodMasters('restaurant_opening_hours'),
    }),
    enabled: open,
  });

  const { data: merchants = [], isFetched: merchantsFetched, isFetching: merchantsFetching } = useQuery({
    queryKey: ['merchant-partners-select'],
    queryFn: async () => {
      const res = await listPartners({ entityType: 'merchant', limit: 200 });
      return res.data;
    },
    enabled: open,
    staleTime: 60_000,
  });

  const merchantsReady = !open || (merchantsFetched && !merchantsFetching);

  const merchantOptions = useMemo(() => {
    const byId = new Map(merchants.map((m) => [m._id, m]));
    const seen = new Set<string>();
    const out: { id: string; label: string }[] = [];

    const push = (id: string, label: string) => {
      if (!id || seen.has(id)) return;
      seen.add(id);
      out.push({ id, label });
    };

    if (form.merchantId) {
      const linked = byId.get(form.merchantId);
      push(
        form.merchantId,
        linked
          ? `${linked.firstName} ${linked.lastName || ''} · ${linked.phone || linked.email}`
          : `Linked merchant (${form.merchantId.slice(-6)})`,
      );
    }
    for (const m of merchants) {
      push(m._id, `${m.firstName} ${m.lastName || ''} · ${m.phone || m.email}`);
    }
    return out;
  }, [form.merchantId, merchants]);

  const merchantSelectValue = form.merchantId && merchantOptions.some((o) => o.id === form.merchantId)
    ? form.merchantId
    : '';

  const { data: merchantPlans = [], isLoading: merchantPlansLoading, isFetched: merchantPlansFetched } = useQuery({
    queryKey: ['merchant-subscription-plans', form.zoneCode],
    queryFn: () => listPartnerSubscriptionPlans({ partnerType: 'merchant', active: true }),
    enabled: open && form.revenueModel === 'subscription',
    staleTime: 60_000,
  });

  const merchantPlansReady = form.revenueModel !== 'subscription' || !open || merchantPlansFetched;

  const subscriptionPlanOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: { id: string; label: string }[] = [];
    const push = (id: string, label: string) => {
      if (!id || seen.has(id)) return;
      seen.add(id);
      out.push({ id, label });
    };
    if (form.partnerSubscriptionPlanId) {
      const linked = merchantPlans.find((p) => p._id === form.partnerSubscriptionPlanId);
      push(
        form.partnerSubscriptionPlanId,
        linked
          ? `${linked.name} · ${formatMoney(linked.price)}/${linked.billingInterval} · ${linked.zoneCode}`
          : `Current plan (${form.partnerSubscriptionPlanId.slice(-6)})`,
      );
    }
    for (const p of merchantPlans) {
      push(p._id, `${p.name} · ${formatMoney(p.price)}/${p.billingInterval} · ${p.zoneCode}`);
    }
    return out;
  }, [form.partnerSubscriptionPlanId, merchantPlans]);

  const subscriptionPlanValue = merchantPlansReady
    && subscriptionPlanOptions.some((o) => o.id === form.partnerSubscriptionPlanId)
    ? form.partnerSubscriptionPlanId
    : '';

  const merchantMissingFromList = !!form.merchantId
    && merchantsReady
    && !merchants.some((m) => m._id === form.merchantId);

  const subscriptionPlanMissingFromList = !!form.partnerSubscriptionPlanId
    && merchantPlansReady
    && !merchantPlans.some((p) => p._id === form.partnerSubscriptionPlanId);

  const { data: merchantComplianceData } = useQuery({
    queryKey: ['merchant-compliance', form.merchantId],
    queryFn: () => getPartnerDocuments(form.merchantId, 'merchant'),
    enabled: open && !!form.merchantId,
  });

  const merchantCompliance = useMemo(
    () => (merchantComplianceData ? extractMerchantCompliance(merchantComplianceData) : null),
    [merchantComplianceData],
  );

  const applyMerchantCompliance = (includeLocation = false) => {
    if (!merchantCompliance) return;
    patch({
      gstNumber: merchantCompliance.gstNumber || form.gstNumber,
      fssaiNumber: merchantCompliance.fssaiNumber || form.fssaiNumber,
      tradeLicenseNumber: merchantCompliance.tradeLicenseNumber || form.tradeLicenseNumber,
      ownerName: merchantCompliance.ownerName || form.ownerName,
      addressLine: merchantCompliance.address || form.addressLine,
      city: merchantCompliance.city || form.city,
      ...(includeLocation && merchantCompliance.latitude ? { latitude: merchantCompliance.latitude } : {}),
      ...(includeLocation && merchantCompliance.longitude ? { longitude: merchantCompliance.longitude } : {}),
    });
  };

  const slug = normalizeRestaurantSlug(form.slug || form.name);

  const duplicateErrors = useMemo(
    () => findRestaurantDuplicateErrors(existingRestaurants, form, editing?._id as string | undefined),
    [existingRestaurants, form.name, form.slug, editing?._id],
  );

  const branchOptions = useMemo(() => {
    const rows = masters?.restaurant_branches || [];
    if (!slug) return rows;
    return rows.filter((b) => String(b.metadata?.restaurantSlug || '') === slug);
  }, [masters?.restaurant_branches, slug]);

  useEffect(() => {
    if (open) {
      setTab(0);
      setTouched({});
    }
  }, [open, editing?._id]);

  useEffect(() => {
    if (serverFieldErrors.name || serverFieldErrors.slug) {
      setTab(0);
      setTouched((t) => ({ ...t, name: true, slug: true }));
    }
  }, [serverFieldErrors.name, serverFieldErrors.slug]);

  // Ensure Business Type is always a valid master code (create + edit).
  useEffect(() => {
    if (!open || mastersLoading) return;
    const types = masters?.restaurant_types || [];
    if (!types.length) return;
    if (!form.restaurantTypeCode || !types.some((t) => t.code === form.restaurantTypeCode)) {
      const fromExisting = types.find(
        (t) =>
          t.code === form.restaurantTypeCode ||
          t.name.toLowerCase() === String(form.restaurantTypeCode || '').toLowerCase(),
      );
      patch({ restaurantTypeCode: fromExisting?.code || types[0].code });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing?._id, mastersLoading, masters?.restaurant_types]);

  // Map legacy cuisine display names → master codes so chips + validation work on edit.
  useEffect(() => {
    if (!open || mastersLoading) return;
    const cuisines = masters?.cuisine_types || [];
    if (!cuisines.length || !form.cuisineTypeCodes.length) return;
    const codeSet = new Set(cuisines.map((c) => c.code));
    const nameToCode = new Map(cuisines.map((c) => [c.name.toLowerCase(), c.code]));
    const mapped = [...new Set(
      form.cuisineTypeCodes
        .map((raw) => (codeSet.has(raw) ? raw : nameToCode.get(raw.toLowerCase()) || ''))
        .filter(Boolean),
    )];
    const same =
      mapped.length === form.cuisineTypeCodes.length &&
      mapped.every((c, i) => c === form.cuisineTypeCodes[i]);
    if (!same && mapped.length > 0) {
      patch({ cuisineTypeCodes: mapped });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing?._id, mastersLoading, masters?.cuisine_types]);

  const toggleCuisine = (code: string) => {
    const next = form.cuisineTypeCodes.includes(code)
      ? form.cuisineTypeCodes.filter((c) => c !== code)
      : [...form.cuisineTypeCodes, code];
    patch({ cuisineTypeCodes: next });
  };

  const updateHour = (index: number, partial: Partial<OpeningHourRow>) => {
    const openingHours = [...form.openingHours];
    openingHours[index] = { ...openingHours[index], ...partial };
    patch({ openingHours });
  };

  const documentsUrl = form.merchantId
    ? `/config/documents?entityType=merchant&partnerId=${form.merchantId}`
    : '/config/documents?entityType=merchant';

  const missingCuisines = !form.cuisineTypeCodes.length;
  const missingType = !form.restaurantTypeCode;
  const missingSubPlan = form.revenueModel === 'subscription' && !form.partnerSubscriptionPlanId;

  const phoneValidation = useMemo(() => {
    const phoneLocal = localPhoneDigits(form.phone);
    const supportLocal = localPhoneDigits(form.supportNumber);
    let phone: string | undefined;
    let supportPhone: string | undefined;

    if (phoneLocal) {
      if (!form.phoneCountryCode.trim()) {
        phone = 'Select a country code before entering the phone number';
      } else {
        phone = validateLocalPhoneForCountry(
          phoneLocal,
          phoneLengthForDialCode(form.phoneCountryCode),
          'Registered phone number',
          { dialCode: form.phoneCountryCode },
        );
      }
    }

    if (supportLocal) {
      if (!form.supportPhoneCountryCode.trim()) {
        supportPhone = 'Select a country code before entering the support number';
      } else {
        supportPhone = validateLocalPhoneForCountry(
          supportLocal,
          phoneLengthForDialCode(form.supportPhoneCountryCode),
          'Support number',
          { dialCode: form.supportPhoneCountryCode },
        );
      }
    }

    return { phone, supportPhone, phoneLocal, supportLocal };
  }, [form.phone, form.phoneCountryCode, form.supportNumber, form.supportPhoneCountryCode]);

  const emailValidation = useMemo(() => {
    const trimmed = form.email.trim();
    if (!trimmed) return { error: undefined as string | undefined, isValid: false };
    const error = validateEmail(trimmed, 'Registered email');
    return { error, isValid: !error };
  }, [form.email]);

  const nameError = !form.name.trim()
    ? 'Restaurant name is required'
    : duplicateErrors.name || serverFieldErrors.name;
  const slugError = !editing
    ? (duplicateErrors.slug || serverFieldErrors.slug)
    : serverFieldErrors.slug;
  const showNameError = Boolean(nameError && (touched.name || form.name.length > 0 || serverFieldErrors.name));
  const showSlugError = Boolean(slugError && (touched.slug || form.slug.length > 0 || serverFieldErrors.slug));
  const showPhoneError = Boolean(
    phoneValidation.phone && (touched.phone || phoneValidation.phoneLocal.length > 0),
  );
  const showSupportPhoneError = Boolean(
    phoneValidation.supportPhone && (touched.supportPhone || phoneValidation.supportLocal.length > 0),
  );
  const showEmailError = Boolean(
    emailValidation.error && (touched.email || form.email.length > 0),
  );
  const phoneWelcomeMessage = form.sendWelcomeEmail && !editing
    ? welcomeEmailHelper('phone', welcomeConfig)
    : 'Valid registered number.';
  const emailWelcomeMessage = form.sendWelcomeEmail && !editing
    ? welcomeEmailHelper('email', welcomeConfig)
    : 'Valid registered email.';
  const welcomeConfigReady = welcomeConfig?.ready ?? false;
  const showWelcomeSetupAlert = !editing && form.sendWelcomeEmail && welcomeConfig && !welcomeConfigReady;

  const canSubmit = !!form.name.trim()
    && !missingCuisines
    && !missingType
    && !missingSubPlan
    && !phoneValidation.phone
    && !phoneValidation.supportPhone
    && !emailValidation.error
    && !duplicateErrors.name
    && (!editing ? !duplicateErrors.slug : true);

  return (
    <FormDrawer
      open={open}
      title={editing ? 'Edit Restaurant Master' : 'Add Restaurant Master'}
      subtitle="Swiggy-style restaurant profile — basic, business, compliance, timing, approval, and revenue model."
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel={editing ? 'Save' : 'Create'}
      loading={loading}
      disableSubmit={!canSubmit}
      width={720}
    >
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>
      )}
      {!canSubmit && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {missingType && 'Select a Business Type on the Business tab. '}
          {missingCuisines && 'Select at least one Cuisine on the Business tab. '}
          {missingSubPlan && 'Select a subscription plan on the Approval tab. '}
          {phoneValidation.phone && 'Fix the registered phone number on the Basic tab. '}
          {phoneValidation.supportPhone && 'Fix the support number on the Basic tab. '}
          {emailValidation.error && 'Fix the registered email on the Basic tab. '}
          {(duplicateErrors.name || serverFieldErrors.name) && 'Fix duplicate restaurant name on the Basic tab. '}
          {(duplicateErrors.slug || serverFieldErrors.slug) && 'Fix duplicate slug on the Basic tab. '}
          {!form.name.trim() && 'Restaurant name is required. '}
        </Alert>
      )}
      {!mastersLoading && !(masters?.restaurant_types?.length) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Seed food masters first: Food Delivery → Masters (restaurant types, cuisines, opening hours).
        </Alert>
      )}
      {showWelcomeSetupAlert && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Welcome email is not fully configured yet.
          {!welcomeConfig?.smtpConfigured && ' Set SMTP_HOST, SMTP_USER, SMTP_PASS, and EMAIL_FROM in backend `.env`.'}
          {!welcomeConfig?.supportEmail && !welcomeEmailConfig.supportEmail && ' Set SUPPORT_EMAIL in backend `.env` and VITE_SUPPORT_EMAIL in frontend `.env`.'}
          {!welcomeConfig?.enabled && ' Set WELCOME_EMAIL_ENABLED=true in backend `.env`.'}
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {RESTAURANT_MASTER_TABS.map((t) => (
          <Tab key={t.id} label={t.label} sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44 }} />
        ))}
      </Tabs>

      {tab === 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight={700}>Basic Details</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Identity, contact, location, and branding.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Restaurant Name *"
              value={form.name}
              error={showNameError}
              helperText={showNameError ? nameError : 'Display name shown in customer apps and admin lists'}
              onBlur={() => setTouched((t) => ({ ...t, name: true }))}
              onChange={(e) => patch({
                name: e.target.value,
                slug: form.slug ? form.slug : normalizeRestaurantSlug(e.target.value),
              })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <PersonNameField fullWidth label="Owner Name" value={form.ownerName}
              onChange={(e) => patch({ ownerName: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Slug *"
              value={form.slug}
              disabled={!!editing}
              error={showSlugError}
              helperText={
                showSlugError
                  ? slugError
                  : editing
                    ? `Locked after create — branch key: ${slug || '—'}`
                    : `Unique URL key for branches — auto-filled from name: ${slug || '—'}`
              }
              onBlur={() => setTouched((t) => ({ ...t, slug: true }))}
              onChange={(e) => patch({ slug: normalizeRestaurantSlug(e.target.value) })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth label="Merchant Account" value={merchantSelectValue}
              disabled={!merchantsReady}
              onChange={(e) => patch({ merchantId: e.target.value })}
              helperText={!merchantsReady
                ? 'Loading merchants…'
                : merchantMissingFromList
                  ? 'Linked merchant not in current list — still saved on this restaurant'
                  : 'Link to merchant for onboarding documents & approval'}>
              <MenuItem value="">— Select merchant —</MenuItem>
              {merchantOptions.map((m) => (
                <MenuItem key={m.id} value={m.id}>{m.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box>
              <PhoneWithDialCodeField
                label="Registered Phone No"
                layout="inline"
                phone={form.phone}
                countryCode={form.phoneCountryCode}
                preferredCountry="IN"
                hideHelper={Boolean(phoneValidation.phoneLocal && !phoneValidation.phone)}
                onChange={({ phone, countryCode }) => patch({ phone, phoneCountryCode: countryCode })}
                onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
                error={showPhoneError}
                helperText={
                  showPhoneError
                    ? phoneValidation.phone
                    : 'Select country code, then enter the registered mobile number'
                }
              />
              {phoneValidation.phoneLocal && !phoneValidation.phone && (
                <FormHelperText sx={{ color: 'success.main', mx: '14px', mt: 0 }}>
                  {phoneWelcomeMessage}
                </FormHelperText>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="email"
              label="Registered Email"
              value={form.email}
              error={showEmailError}
              helperText={
                emailValidation.isValid
                  ? emailWelcomeMessage
                  : showEmailError
                    ? emailValidation.error
                    : 'Restaurant contact email for onboarding and account notices'
              }
              FormHelperTextProps={
                emailValidation.isValid ? { sx: { color: 'success.main' } } : undefined
              }
              onBlur={() => setTouched((t) => ({ ...t, email: true }))}
              onChange={(e) => patch({ email: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <PhoneWithDialCodeField
              label="Support Number"
              layout="inline"
              phone={form.supportNumber}
              countryCode={form.supportPhoneCountryCode}
              preferredCountry="IN"
              onChange={({ phone, countryCode }) => patch({ supportNumber: phone, supportPhoneCountryCode: countryCode })}
              onBlur={() => setTouched((t) => ({ ...t, supportPhone: true }))}
              error={showSupportPhoneError}
              helperText={
                showSupportPhoneError
                  ? phoneValidation.supportPhone
                  : 'Customer support line shown in apps and admin lists'
              }
            />
          </Grid>
          {!editing && (
            <Grid item xs={12}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={form.sendWelcomeEmail}
                    onChange={(e) => patch({ sendWelcomeEmail: e.target.checked })}
                  />
                )}
                label="Send welcome message (email and/or SMS when configured)"
              />
              {welcomeConfig?.supportEmail || welcomeEmailConfig.supportEmail ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4.5, mt: -0.5 }}>
                  Support / reply-to: {welcomeConfig?.supportEmail || welcomeEmailConfig.supportEmail}
                </Typography>
              ) : null}
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={8}
              label="Notes / Description"
              placeholder="e.g. Pure veg kitchen, famous for biryani, open till midnight, delivery within 5 km…"
              value={form.description}
              inputProps={{ maxLength: 500 }}
              helperText={`${form.description.length}/500 characters — shown on the restaurant profile in customer apps and admin lists`}
              onBlur={() => setTouched((t) => ({ ...t, description: true }))}
              onChange={(e) => patch({ description: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Address" value={form.addressLine}
              onChange={(e) => patch({ addressLine: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="City" value={form.city} onChange={(e) => patch({ city: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="State" value={form.state} onChange={(e) => patch({ state: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Pincode" value={form.pincode} onChange={(e) => patch({ pincode: e.target.value })} />
          </Grid>
          <Grid item xs={12}>
            <RestaurantLocationPicker
              latitude={form.latitude}
              longitude={form.longitude}
              onChange={(latitude, longitude) => patch({ latitude, longitude })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ZoneMasterSelect label="Zone" value={form.zoneCode} onChange={(code) => patch({ zoneCode: code })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormFileUpload label="Logo" value={form.logo} onChange={(url) => patch({ logo: url })}
              folder="food/restaurants/logo"
              imageConstraints={{ minWidth: 200, minHeight: 200, maxWidth: 800, maxHeight: 800, aspectRatio: '1:1' }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormFileUpload label="Cover Image" value={form.coverImage} onChange={(url) => patch({ coverImage: url })}
              folder="food/restaurants/cover"
              imageConstraints={{
                minWidth: FOOD_IMAGE_SPECS.restaurantType.minWidth,
                minHeight: FOOD_IMAGE_SPECS.restaurantType.minHeight,
                maxWidth: FOOD_IMAGE_SPECS.restaurantType.maxWidth,
                maxHeight: FOOD_IMAGE_SPECS.restaurantType.maxHeight,
                aspectRatio: FOOD_IMAGE_SPECS.restaurantType.aspectRatio,
              }} />
          </Grid>
        </Grid>
      )}

      {tab === 1 && (
        <Grid container spacing={2}>
          {form.merchantId && merchantCompliance && hasMerchantCompliance(merchantCompliance) && (
            <Grid item xs={12}>
              <Alert
                severity="success"
                action={(
                  <Stack direction="row" spacing={1}>
                    <Button size="small" color="inherit" onClick={() => applyMerchantCompliance(false)}>
                      Apply compliance
                    </Button>
                    {(merchantCompliance.latitude || merchantCompliance.longitude) && (
                      <Button size="small" color="inherit" onClick={() => applyMerchantCompliance(true)}>
                        + location
                      </Button>
                    )}
                  </Stack>
                )}
              >
                Approved merchant documents / onboarding available
                {merchantCompliance.sources.length ? ` (${merchantCompliance.sources.join(', ')})` : ''}.
                GST: {merchantCompliance.gstNumber || '—'} · FSSAI: {merchantCompliance.fssaiNumber || '—'}
              </Alert>
            </Grid>
          )}
          {form.merchantId && merchantComplianceData && merchantCompliance && !hasMerchantCompliance(merchantCompliance) && (
            <Grid item xs={12}>
              <Alert severity="warning">
                No approved GST/FSSAI documents yet for this merchant. Submit and approve documents in onboarding first.
              </Alert>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth required label="Business Type"
              value={mastersLoading ? '' : (form.restaurantTypeCode || '')}
              disabled={mastersLoading || !(masters?.restaurant_types?.length)}
              onChange={(e) => patch({ restaurantTypeCode: e.target.value })}>
              <MenuItem value="">— Select —</MenuItem>
              {(masters?.restaurant_types || []).map((t) => (
                <MenuItem key={t.code} value={t.code}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {masterImageUrl(t) && <Avatar src={resolveAssetUrl(masterImageUrl(t))} sx={{ width: 24, height: 24 }} />}
                    <span>{t.name}</span>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth label="Branch (optional)" value={form.branchCode}
              onChange={(e) => patch({ branchCode: e.target.value })}>
              <MenuItem value="">— None —</MenuItem>
              {branchOptions.map((b) => (
                <MenuItem key={b.code} value={b.code}>{b.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>Cuisines *</Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {(masters?.cuisine_types || []).map((c) => {
                const selected = form.cuisineTypeCodes.includes(c.code);
                const img = masterImageUrl(c);
                return (
                  <Chip key={c.code} label={c.name}
                    avatar={img ? <Avatar src={resolveAssetUrl(img)} /> : undefined}
                    color={selected ? 'primary' : 'default'}
                    variant={selected ? 'filled' : 'outlined'}
                    onClick={() => toggleCuisine(c.code)} />
                );
              })}
            </Stack>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="GST Number" value={form.gstNumber}
              onChange={(e) => patch({ gstNumber: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="FSSAI Number" value={form.fssaiNumber}
              onChange={(e) => patch({ fssaiNumber: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Trade License" value={form.tradeLicenseNumber}
              onChange={(e) => patch({ tradeLicenseNumber: e.target.value })} />
          </Grid>
        </Grid>
      )}

      {tab === 2 && (
        <Grid container spacing={2}>
          <Grid item xs={12} display="flex" flexWrap="wrap" gap={2}>
            <FormControlLabel control={<Switch checked={form.isPureVeg} onChange={(e) => patch({ isPureVeg: e.target.checked })} />} label="Pure Veg" />
            <FormControlLabel control={<Switch checked={form.deliveryEnabled} onChange={(e) => patch({ deliveryEnabled: e.target.checked })} />} label="Delivery" />
            <FormControlLabel control={<Switch checked={form.takeawayEnabled} onChange={(e) => patch({ takeawayEnabled: e.target.checked })} />} label="Takeaway" />
            <FormControlLabel control={<Switch checked={form.dineInEnabled} onChange={(e) => patch({ dineInEnabled: e.target.checked })} />} label="Dine In" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth type="number" label={fieldLabel('Min Order')} value={form.minOrderAmount}
              onChange={(e) => patch({ minOrderAmount: Number(e.target.value) })} />
          </Grid>
          <Grid item xs={12} display="flex" gap={2}>
            <FormControlLabel control={<Switch checked={form.isOpen} onChange={(e) => patch({ isOpen: e.target.checked })} />} label="Open Now" />
            <FormControlLabel control={<Switch checked={form.isActive} onChange={(e) => patch({ isActive: e.target.checked })} />} label="Active Listing" />
          </Grid>
        </Grid>
      )}

      {tab === 3 && (
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Weekly Schedule</Typography>
            <Button size="small" variant="outlined" disabled={!masters?.restaurant_opening_hours?.length}
              onClick={() => {
                const templates = masters?.restaurant_opening_hours || [];
                if (templates.length) patch({ openingHours: openingHoursFromMasters(templates) });
              }}>
              Apply from masters
            </Button>
          </Stack>
          {form.openingHours.map((row, i) => {
            const dayLabel = WEEK_DAYS.find((d) => d.value === row.day)?.label || row.day;
            return (
              <Grid container spacing={1} key={row.day} sx={{ mb: 1 }} alignItems="center">
                <Grid item xs={2} sm={2}><Typography variant="body2">{dayLabel}</Typography></Grid>
                <Grid item xs={4} sm={3}>
                  <TextField fullWidth size="small" label="Open" value={row.openTime} disabled={row.isClosed}
                    onChange={(e) => updateHour(i, { openTime: e.target.value })} />
                </Grid>
                <Grid item xs={4} sm={3}>
                  <TextField fullWidth size="small" label="Close" value={row.closeTime} disabled={row.isClosed}
                    onChange={(e) => updateHour(i, { closeTime: e.target.value })} />
                </Grid>
                <Grid item xs={2}>
                  <FormControlLabel
                    control={<Switch size="small" checked={!!row.isClosed} onChange={(e) => updateHour(i, { isClosed: e.target.checked })} />}
                    label="Closed"
                  />
                </Grid>
              </Grid>
            );
          })}
          <TextField select fullWidth label="Weekly Holiday" value={form.weeklyHoliday} sx={{ mt: 2 }}
            onChange={(e) => patch({ weeklyHoliday: e.target.value })}
            helperText="Optional full-day off (in addition to per-day closed switches)">
            <MenuItem value="">— None —</MenuItem>
            {WEEK_DAYS.map((d) => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
          </TextField>
        </Box>
      )}

      {tab === 4 && (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth label="Approval Status" value={form.verificationStatus}
              onChange={(e) => patch({ verificationStatus: e.target.value as RestaurantFormState['verificationStatus'] })}>
              {VERIFICATION_STATUS_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth label="Revenue Model" value={form.revenueModel}
              onChange={(e) => patch({ revenueModel: e.target.value as RestaurantFormState['revenueModel'] })}>
              <MenuItem value="commission">Commission based</MenuItem>
              <MenuItem value="subscription">Subscription based</MenuItem>
            </TextField>
          </Grid>
          {form.revenueModel === 'commission' && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth label="Commission Type" value={form.vendorCommissionType}
                  onChange={(e) => patch({ vendorCommissionType: e.target.value as 'percentage' | 'fixed' })}>
                  <MenuItem value="percentage">Percentage (%)</MenuItem>
                  <MenuItem value="fixed">{fieldLabel('Fixed')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth type="number" label="Commission Value"
                  value={form.vendorCommissionValue}
                  onChange={(e) => patch({ vendorCommissionValue: Number(e.target.value) })} />
              </Grid>
            </>
          )}
          {form.revenueModel === 'subscription' && (
            <Grid item xs={12}>
              <TextField select fullWidth required label="Merchant Subscription Plan"
                value={subscriptionPlanValue}
                disabled={!merchantPlansReady || merchantPlansLoading}
                onChange={(e) => patch({ partnerSubscriptionPlanId: e.target.value })}
                helperText="Flat-fee plan — commission waived (create under Transport → Partner Subscription Plans, type Merchant)">
                <MenuItem value="">— Select plan —</MenuItem>
                {subscriptionPlanOptions.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          <Grid item xs={12}>
            <Alert severity="info">
              Pending restaurants are hidden from customer apps until status is <strong>Approved</strong>.
              Blocked restaurants are deactivated automatically.
            </Alert>
          </Grid>
        </Grid>
      )}

      {tab === 5 && (
        <Stack spacing={2}>
          <Alert severity="info">
            Merchant KYC documents (GST, FSSAI, trade license, owner ID) are submitted during partner onboarding
            and reviewed in Document Management. On approval, restaurant details sync automatically.
          </Alert>
          {form.merchantId && merchantCompliance && hasMerchantCompliance(merchantCompliance) && (
            <Button variant="outlined" onClick={() => { applyMerchantCompliance(true); setTab(1); }}>
              Apply GST / FSSAI from approved documents
            </Button>
          )}
          <Button
            component={RouterLink}
            to={documentsUrl}
            target="_blank"
            variant="contained"
            endIcon={<OpenInNew />}
            disabled={!form.merchantId}
          >
            Review merchant documents
          </Button>
          {!form.merchantId && (
            <Typography variant="caption" color="text.secondary">
              Select a merchant account on the Basic tab to open their document queue.
            </Typography>
          )}
          <Button component={RouterLink} to="/onboarding" target="_blank" variant="outlined" endIcon={<OpenInNew />}>
            Open partner onboarding hub
          </Button>
        </Stack>
      )}
    </FormDrawer>
  );
}
